import {gql} from '@apollo/client';
import React, {useState} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipSelectInput from 'ui-library/inputs/ChipSelectInput';
import {OptionData, OptionValue} from 'ui-library/menus/MenuConstants';

import {
  ProcedureRunsForSelectorQuery,
  ProcedureRunsForSelectorQueryVariables,
} from './__generated/ProcedureRunsForSelectorQuery';

export const PROCEDURE_RUNS_FOR_SELECTOR_QUERY = gql`
  query ProcedureRunsForSelectorQuery($studyId: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    study(id: $studyId) {
      ... on StudyNode {
        id
        procedureRuns {
          ... on ProcedureRunNode {
            id
            name
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface QueryingProcedureRunSelectorByStudyProps {
  studyId: string | null;
  selectedRunIds: Array<string>;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
  isMulti?: boolean;
  maxItems?: number;
  placeholder?: string;
}

export default function QueryingProcedureRunSelectorByStudy({
  studyId,
  selectedRunIds,
  onSelect,
  onRemove,
  disabled,
  isMulti,
  maxItems,
  placeholder,
}: QueryingProcedureRunSelectorByStudyProps): JSX.Element {
  const queryResult = useSkippableBenchlingQuery<
    ProcedureRunsForSelectorQuery,
    ProcedureRunsForSelectorQueryVariables
  >(PROCEDURE_RUNS_FOR_SELECTOR_QUERY, {
    variables: {
      studyId: studyId!,
    },
    skip: studyId == null,
  });

  let allRuns: Array<{id: string; name: string}> = [];

  if (queryResult.type === QueryResultType.SUCCESS) {
    const study = queryResult.data.study;
    if (!isInaccessible(study)) {
      allRuns = accessibleObjs(study.procedureRuns).map((run) => ({
        id: run.id,
        name: run.name,
      }));
    }
  }

  const isLoading = queryResult.type === QueryResultType.PENDING;
  const hasNoStudy = studyId == null;

  return (
    <ProcedureRunSelectorByStudy
      allRuns={allRuns}
      selectedRunIds={selectedRunIds}
      onSelect={onSelect}
      onRemove={onRemove}
      disabled={disabled || isLoading || hasNoStudy}
      isMulti={isMulti}
      maxItems={maxItems}
      placeholder={placeholder}
    />
  );
}

type ProcedureRunSelectorByStudyProps = Omit<QueryingProcedureRunSelectorByStudyProps, 'studyId'> & {
  allRuns: Array<{id: string; name: string}>;
};

/**
 * Chip-select input for picking procedure runs within a study.
 * Displays run names as chips and supports filtering by query string.
 */
export function ProcedureRunSelectorByStudy({
  allRuns,
  selectedRunIds,
  onSelect,
  onRemove,
  disabled,
  isMulti = true,
  maxItems,
  placeholder = 'Select recipe or assay run',
}: ProcedureRunSelectorByStudyProps): JSX.Element {
  const [query, setQuery] = useState('');

  const nameById = new Map(allRuns.map((run) => [run.id, run.name]));
  const selectedSet = new Set(selectedRunIds);
  const lowerQuery = query.toLowerCase();

  const filteredRuns = allRuns.filter(
    (run) => !selectedSet.has(run.id) && run.name.toLowerCase().includes(lowerQuery)
  );

  const optionDatas: Array<OptionData> = filteredRuns.map((run) => ({
    name: run.name,
    value: {id: run.id},
  }));

  const displayValue = selectedRunIds.map((id) => ({id}));

  return (
    <ChipSelectInput
      disabled={disabled}
      isMulti={isMulti}
      maxItems={maxItems}
      optionDatas={optionDatas}
      value={displayValue}
      getChipProps={(value: OptionValue) => ({
        text: nameById.get(value?.id) ?? value?.id ?? '',
        size: ChipSize.MINI,
      })}
      onQueryChange={(e) => {
        setQuery(e.target.value);
      }}
      query={query}
      onSelect={(value: OptionValue) => {
        if (value?.id != null) {
          onSelect(value.id);
          setQuery('');
        }
      }}
      onRemove={(value: OptionValue) => {
        if (value?.id != null) {
          onRemove(value.id);
        }
      }}
      placeholder={placeholder}
    />
  );
}
